import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingBillingHistory() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [clients, setClients] = useState([]);

  const [selected, setSelected] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => { if (companyId) { fetchHistory(); loadClients(); } }, [companyId, page, clientFilter, statusFilter, yearFilter]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchHistory = async () => {
    setLoading(true);
    let url = `${API}/companies/${companyId}/outsourcing/billing?page=${page}`;
    if (clientFilter) url += `&client_id=${clientFilter}`;
    if (statusFilter) url += `&billing_status=${statusFilter}`;
    if (yearFilter) url += `&year=${yearFilter}`;
    // Show all statuses including historical
    const res = await authFetch(url);
    if (!res) return;
    const json = await res.json();
    setPeriods(json.data || []);
    setLastPage(json.last_page || 1);
    setLoading(false);
  };

  const loadClients = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients`);
    if (res) { const j = await res.json(); setClients(j.data || []); }
  };

  const openPeriod = async (p) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${p.id}`);
    if (!res) return;
    setSelected(await res.json());
    loadAudit(p.id);
  };

  const loadAudit = async (periodId) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing-audit?period_id=${periodId}`);
    if (res) { const j = await res.json(); setAuditLogs(j.data || []); }
  };

  const exportBilling = async (periodId) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${periodId}/export`);
    if (!res) return;
    const data = await res.json();
    if (!data.rows || data.rows.length === 0) { window.alert('No data to export.'); return; }
    const csv = [Object.keys(data.rows[0]).join(','), ...data.rows.map(r => Object.values(r).join(','))].join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `billing_history_${periodId}.csv`;
    a.click();
  };

  if (!companyId) return <div style={st.page}><p>Please select a company first.</p></div>;

  // ── DETAIL VIEW ──
  if (selected) {
    const p = selected;
    return (
      <div style={st.page}>
        <button onClick={() => setSelected(null)} style={st.backBtn}>← Back to History</button>
        <div style={st.detailHeader}>
          <h2 style={st.title}>{p.client?.name || '—'} — {p.billing_month}/{p.billing_year} <span style={{fontSize:14,color:'#999'}}>v{p.version}</span></h2>
          <div style={{display:'flex',gap:8}}>
            <span style={{...st.badge,backgroundColor:billingClr(p.billing_status)}}>{p.billing_status}</span>
            <button onClick={() => exportBilling(p.id)} style={st.btnSm}>Export CSV</button>
          </div>
        </div>

        {/* Totals Summary */}
        <div style={{...st.section,display:'flex',gap:24,flexWrap:'wrap',justifyContent:'space-around'}}>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Standard Services</div><div style={{fontSize:20,fontWeight:700}}>${Number(p.total_standard_services||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Extra Services</div><div style={{fontSize:20,fontWeight:700}}>${Number(p.total_extra_services||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Adjustments</div><div style={{fontSize:20,fontWeight:700}}>${Number(p.total_adjustments||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Total Billable</div><div style={{fontSize:24,fontWeight:700,color:'#27ae60'}}>${Number(p.total_billable||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
        </div>

        {/* Snapshot Data */}
        {p.snapshot_data && (
          <div style={st.section}>
            <h3 style={{marginTop:0,marginBottom:12}}>Approved Snapshot</h3>
            <p style={{fontSize:13,color:'#999'}}>This data was frozen at approval time and represents the historical source of truth.</p>
            <pre style={{background:'#f8f9fa',padding:12,borderRadius:6,overflow:'auto',fontSize:12,maxHeight:300}}>
              {JSON.stringify(p.snapshot_data, null, 2)}
            </pre>
          </div>
        )}

        {/* Employee Records */}
        <div style={st.section}>
          <h3 style={{marginTop:0}}>Employee Billing Records</h3>
          <div style={st.tableWrap}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Employee</th>
                  <th style={st.th}>Service</th>
                  <th style={st.th}>Salary</th>
                  <th style={st.th}>Rate</th>
                  <th style={st.th}>Transport</th>
                  <th style={st.th}>Insurance</th>
                  <th style={st.th}>Proration</th>
                  <th style={st.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(p.records || []).map((r, i) => (
                  <tr key={r.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                    <td style={st.td}>{r.employee?.name || `#${r.employee_id}`}</td>
                    <td style={st.td}>{r.service?.name || '—'}</td>
                    <td style={st.td}>${Number(r.outsourcing_salary||0).toLocaleString()}</td>
                    <td style={st.td}>${Number(r.billing_rate||0).toLocaleString()}</td>
                    <td style={st.td}>${Number(r.transportation_amount||0).toLocaleString()}</td>
                    <td style={st.td}>${Number(r.insurance_amount||0).toLocaleString()}</td>
                    <td style={st.td}>{(r.proration_factor * 100).toFixed(0)}%</td>
                    <td style={{...st.td,fontWeight:600}}>${Number(r.employee_billable_amount||0).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Workflow History */}
        <div style={st.section}>
          <h3 style={{marginTop:0}}>Workflow History</h3>
          <div style={st.grid2}>
            <div><strong>Generated By:</strong> {p.generated_by_user?.name || '—'}</div>
            <div><strong>Reviewed By:</strong> {p.reviewed_by_user?.name || '—'} {p.reviewed_at ? `— ${new Date(p.reviewed_at).toLocaleString()}` : ''}</div>
            <div><strong>Approved By:</strong> {p.approved_by_user?.name || '—'} {p.approved_at ? `— ${new Date(p.approved_at).toLocaleString()}` : ''}</div>
            <div><strong>Approved Total:</strong> {p.approved_total ? `$${Number(p.approved_total).toLocaleString('en-US',{minimumFractionDigits:2})}` : '—'}</div>
            {p.reopened_by && <div><strong>Reopened By:</strong> User #{p.reopened_by} {p.reopened_at ? `— ${new Date(p.reopened_at).toLocaleString()}` : ''}</div>}
            {p.reopen_reason && <div><strong>Reopen Reason:</strong> {p.reopen_reason}</div>}
          </div>
        </div>

        {/* Accounting Handoff */}
        {p.latest_handoff && (
          <div style={st.section}>
            <h3 style={{marginTop:0}}>Latest Accounting Handoff</h3>
            <div style={st.grid2}>
              <div><strong>Status:</strong> <span style={{...st.badge,backgroundColor:handoffClr(p.latest_handoff.handoff_status)}}>{p.latest_handoff.handoff_status}</span></div>
              <div><strong>Sent At:</strong> {p.latest_handoff.sent_at ? new Date(p.latest_handoff.sent_at).toLocaleString() : '—'}</div>
              <div><strong>Invoice #:</strong> {p.latest_handoff.invoice_number || '—'}</div>
              <div><strong>Invoiced Amount:</strong> {p.latest_handoff.invoiced_amount ? `$${Number(p.latest_handoff.invoiced_amount).toLocaleString('en-US',{minimumFractionDigits:2})}` : '—'}</div>
            </div>
          </div>
        )}

        {/* Audit Trail */}
        <div style={st.section}>
          <h3 style={{marginTop:0}}>Audit Trail</h3>
          {auditLogs.length === 0 ? <p style={{color:'#999'}}>No audit logs.</p> : (
            auditLogs.map((log, i) => (
              <div key={i} style={{padding:'6px 0',borderBottom:'1px solid #f0f0f0'}}>
                <div style={{fontSize:13}}>
                  <strong>{log.user?.name || 'System'}</strong>
                  <span style={{color:'#999',marginLeft:8}}>{new Date(log.created_at).toLocaleString()}</span>
                </div>
                <div style={{fontSize:13}}>{log.action}: {log.description}</div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div style={st.page}>
      <h2 style={{...st.title,marginBottom:16}}>Billing History</h2>

      <div style={st.filterRow}>
        <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setPage(1); }} style={{...st.input,width:180}}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{...st.input,width:140}}>
          <option value="">All Statuses</option>
          {['draft','reviewed','approved','invoiced','paid'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={yearFilter} onChange={e => { setYearFilter(e.target.value); setPage(1); }} style={{...st.input,width:100}}>
          <option value="">All Years</option>
          {[2024,2025,2026,2027,2028].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {initialLoading ? <p>Loading...</p> : periods.length === 0 ? <p style={{color:'#999'}}>No billing history found.</p> : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Client</th>
                <th style={st.th}>Period</th>
                <th style={st.th}>Version</th>
                <th style={st.th}>Status</th>
                <th style={st.th}>Total</th>
                <th style={st.th}>Approved Total</th>
                <th style={st.th}>Accounting</th>
                <th style={st.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, i) => (
                <tr key={p.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                  <td style={st.td}>{p.client?.name || '—'}</td>
                  <td style={st.td}>{p.billing_month}/{p.billing_year}</td>
                  <td style={st.td}>v{p.version}</td>
                  <td style={st.td}><span style={{...st.badge,backgroundColor:billingClr(p.billing_status)}}>{p.billing_status}</span></td>
                  <td style={st.td}>${Number(p.total_billable||0).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  <td style={st.td}>{p.approved_total ? `$${Number(p.approved_total).toLocaleString('en-US',{minimumFractionDigits:2})}` : '—'}</td>
                  <td style={st.td}><span style={{...st.badge,backgroundColor:acctClr(p.accounting_status)}}>{p.accounting_status}</span></td>
                  <td style={st.td}>
                    <button onClick={() => openPeriod(p)} style={st.btnSm}>View</button>
                    <button onClick={() => exportBilling(p.id)} style={{...st.btnSm,marginLeft:4}}>Export</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div style={st.pagination}>
          <button disabled={page<=1} onClick={() => setPage(page-1)} style={st.pageBtn}>Prev</button>
          <span>Page {page} of {lastPage}</span>
          <button disabled={page>=lastPage} onClick={() => setPage(page+1)} style={st.pageBtn}>Next</button>
        </div>
      )}
    </div>
  );
}

function billingClr(s) {
  const m = { draft:'#95a5a6', reviewed:'#3498db', approved:'#27ae60', invoiced:'#8e44ad', paid:'#2ecc71' };
  return m[s] || '#95a5a6';
}
function acctClr(s) {
  const m = { not_sent:'#95a5a6', sent:'#3498db', invoice_created:'#2980b9', invoiced:'#8e44ad', paid:'#27ae60' };
  return m[s] || '#95a5a6';
}
function handoffClr(s) {
  const m = { sent:'#3498db', invoice_created:'#2980b9', invoiced:'#8e44ad', paid:'#27ae60', failed:'#e74c3c', cancelled:'#7f8c8d' };
  return m[s] || '#95a5a6';
}

const st = {
  page: { padding:24, maxWidth:1200, margin:'0 auto' },
  title: { fontSize:24, fontWeight:700, margin:0 },
  filterRow: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  input: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14, width:'100%', boxSizing:'border-box', marginBottom:8 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  btnSm: { padding:'4px 10px', background:'#ecf0f1', border:'none', borderRadius:4, cursor:'pointer', fontSize:13 },
  backBtn: { background:'none', border:'none', color:'#3498db', cursor:'pointer', fontSize:14, fontWeight:600, marginBottom:12, padding:0 },
  detailHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:16 },
  grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:16, marginTop:16 },
  pageBtn: { padding:'6px 14px', border:'1px solid #ccc', borderRadius:6, background:'#fff', cursor:'pointer' },
};
